import { ownerPermissions } from "./permissions.ts";
import type { OwnerAdminState, SocialFlags, StaffRole } from "./types.ts";

const now = "2026-06-06T14:30:00.000Z";
const flags: SocialFlags = { around: true, gifting: true, contactRequests: true, games: false };
const rolePerms: Record<StaffRole, string[]> = {
  waiter: ["table:view", "order:view", "order:cancel"],
  runner: ["order:view", "order:fulfil", "gift:fulfil"],
  bartender: ["order:fulfil", "gift:fulfil", "gift:reject"],
  cashier: ["cashier:view", "payment:capture", "refund:request"],
  moderator: ["moderation:view", "moderation:action", "incident:create"],
  floor_manager: ["session:manage", "social:disable", "table:manage", "moderation:action"],
  owner: ["venue:manage", "staff:manage", "roles:assign", "privacy:manage"],
};

export function createInitialOwnerAdminState(): OwnerAdminState {
  return {
    principals: [
      {
        id: "owner-1",
        name: "Aran Owner",
        email: "owner@smokemirrors.example",
        role: "owner",
        authorizedVenueIds: ["venue-smoke", "venue-glass"],
        currentVenueId: "venue-smoke",
        permissions: ownerPermissions.owner,
      },
      {
        id: "manager-1",
        name: "Mali Manager",
        email: "manager@smokemirrors.example",
        role: "manager",
        authorizedVenueIds: ["venue-smoke"],
        currentVenueId: "venue-smoke",
        permissions: ownerPermissions.manager,
      },
    ],
    venues: [
      {
        id: "venue-smoke",
        name: "Smoke & Mirrors",
        nameTh: "สโมค แอนด์ มิเรอร์",
        description: "Consent-first social commerce for an active venue session.",
        lineOaId: "@smokemirrors",
        logoUrl: "mock://brand/smoke-logo-placeholder",
        coverUrl: "mock://brand/smoke-cover-placeholder",
        featureFlags: { ...flags },
      },
      {
        id: "venue-glass",
        name: "Glass Room",
        nameTh: "กลาสรูม",
        description: "Second authorized demo venue.",
        lineOaId: "@glassroom",
        logoUrl: "mock://brand/glass-logo-placeholder",
        coverUrl: "mock://brand/glass-cover-placeholder",
        featureFlags: { ...flags, games: true },
      },
    ],
    currentVenueId: "venue-smoke",
    branches: [
      {
        id: "branch-thonglor",
        venueId: "venue-smoke",
        name: "Thonglor",
        nameTh: "ทองหล่อ",
        address: "Thonglor Soi 10",
        addressTh: "ทองหล่อ ซอย 10",
        operatingHours: "18:00-02:00",
        active: true,
        featureOverrides: { ...flags },
      },
      {
        id: "branch-ari",
        venueId: "venue-smoke",
        name: "Ari",
        nameTh: "อารีย์",
        address: "Ari 4",
        addressTh: "อารีย์ 4",
        operatingHours: "17:00-00:00",
        active: true,
        featureOverrides: { ...flags, contactRequests: false },
      },
    ],
    zones: [
      {
        id: "zone-main",
        branchId: "branch-thonglor",
        name: "Main Floor",
        nameTh: "โซนหลัก",
        sortOrder: 1,
        adjacencyHint: "nearest bar and stage",
      },
      {
        id: "zone-terrace",
        branchId: "branch-thonglor",
        name: "Terrace",
        nameTh: "ระเบียง",
        sortOrder: 2,
        adjacencyHint: "outside smoking area",
      },
      {
        id: "zone-bar",
        branchId: "branch-thonglor",
        name: "Bar",
        nameTh: "บาร์",
        sortOrder: 3,
        adjacencyHint: "bar seats order-only after last call",
      },
    ],
    tables: Array.from({ length: 12 }, (_, index) => ({
      id: `tbl-${index + 1}`,
      branchId: "branch-thonglor",
      zoneId: index < 6 ? "zone-main" : index < 10 ? "zone-terrace" : "zone-bar",
      code: index < 10 ? `T${String(index + 1).padStart(2, "0")}` : `B${index - 9}`,
      label: index < 10 ? `Table ${index + 1}` : `Bar Seat ${index - 9}`,
      capacity: index < 10 ? 4 : 1,
      status: (["free", "occupied", "pending", "disabled"] as const)[index % 4]!,
      orderOnly: index % 5 === 0,
      qrToken: `qr_${cryptoSafe(index + 1)}`,
      qrRotatedAt: now,
      staleQrWarning: index === 3,
    })),
    categories: [
      { id: "cat-cocktails", name: "Cocktails", nameTh: "ค็อกเทล", sortOrder: 1 },
      { id: "cat-beer", name: "Beer", nameTh: "เบียร์", sortOrder: 2 },
      { id: "cat-food", name: "Food", nameTh: "อาหาร", sortOrder: 3 },
    ],
    menuItems: [
      {
        id: "item-old-fashioned",
        categoryId: "cat-cocktails",
        name: "Smoked Old Fashioned",
        nameTh: "โอลด์แฟชั่นรมควัน",
        description: "Signature smoke finish",
        priceMinor: 32000,
        available: true,
        giftable: true,
        alcohol: true,
        featuredGiftable: true,
        inFlightWarning: "2 in-flight orders tonight",
        modifiers: [
          {
            id: "mod-sweet",
            name: "Sweetness",
            required: false,
            multiSelect: false,
            options: [{ id: "less", name: "Less sweet", priceDeltaMinor: 0 }],
          },
        ],
      },
      {
        id: "item-highball",
        categoryId: "cat-cocktails",
        name: "Lime Lemongrass Highball",
        nameTh: "ไฮบอลตะไคร้มะนาว",
        description: "Thai citrus highball",
        priceMinor: 26000,
        available: true,
        giftable: true,
        alcohol: true,
        featuredGiftable: true,
        modifiers: [],
      },
      {
        id: "item-water",
        categoryId: "cat-food",
        name: "Sparkling Water",
        nameTh: "น้ำแร่",
        description: "Non-alcohol option",
        priceMinor: 9000,
        available: true,
        giftable: true,
        alcohol: false,
        featuredGiftable: false,
        modifiers: [],
      },
    ],
    staff: [
      {
        id: "staff-floor",
        name: "Mali Floor",
        username: "mali",
        staffCode: "FLOOR",
        active: true,
        branchScope: ["branch-thonglor"],
        roles: ["floor_manager"],
      },
      {
        id: "staff-bar",
        name: "Ben Bar",
        username: "ben",
        staffCode: "BAR",
        active: true,
        branchScope: ["branch-thonglor"],
        roles: ["bartender", "runner"],
      },
      {
        id: "staff-cash",
        name: "Cash Narin",
        username: "cash",
        staffCode: "CASH",
        active: false,
        branchScope: ["branch-thonglor"],
        roles: ["cashier"],
      },
    ],
    roles: (Object.keys(rolePerms) as StaffRole[]).map((role) => ({
      role,
      label: role.replace(/_/g, " "),
      permissions: rolePerms[role],
      editable: false,
    })),
    socialRules: {
      ...flags,
      defaultMode: "private",
      giftCapPerSession: 3,
      contactLimitPerSession: 1,
      // FR-OWN-117 (OQ-008 pilot decision): default OFF.
      allowAlcoholGifting: false,
    },
    sessionTemplates: [
      {
        id: "tmpl-sat",
        label: "Saturday Night",
        branchId: "branch-thonglor",
        enabledFeatures: { ...flags },
        lastCallRule: "01:30 or floor manager manual",
        expectedCloseTime: "02:00",
      },
    ],
    sessions: [],
    analyticsEvents: [
      {
        id: "aev-1",
        name: "AEV-order_served",
        sessionId: "vs-2026-06-06",
        metadata: { orderCount: 134 },
        createdAt: now,
      },
      {
        id: "aev-2",
        name: "AEV-gift_fulfilled",
        sessionId: "vs-2026-06-06",
        metadata: { giftCount: 27, acceptedRate: 0.74 },
        createdAt: now,
      },
      {
        id: "aev-3",
        name: "AEV-report_submitted",
        sessionId: "vs-2026-06-06",
        metadata: { count: 4 },
        createdAt: now,
      },
    ],
    analyticsSummary: {
      activeTables: 12,
      ordersTonight: 134,
      giftsSent: 27,
      giftAcceptedRate: 0.74,
      estimatedRevenueMinor: 4820000,
      reportsCount: 4,
      series: [
        { label: "20:00", value: 18 },
        { label: "21:00", value: 46 },
        { label: "22:00", value: 70 },
      ],
      rangeFrom: "2026-06-06T13:00:00.000Z",
      rangeTo: now,
      giftFunnel: { sent: 27, accepted: 22, rejected: 3, expired: 2, fulfilled: 20 },
      orderFunnel: {
        pending: 6,
        accepted: 10,
        preparing: 8,
        ready: 4,
        served: 106,
        cancelled: 0,
        rejected: 0,
      },
    },
    incidents: [
      {
        id: "inc-1",
        type: "moderation",
        severity: "critical",
        state: "investigating",
        sessionId: "vs-2026-06-06",
        summary: "Safety escalation; reporter identity withheld.",
        auditIds: ["aud-1"],
      },
      {
        id: "inc-2",
        type: "refund",
        severity: "medium",
        state: "open",
        sessionId: "vs-2026-06-06",
        summary: "Gift refund review",
        auditIds: ["aud-2"],
      },
    ],
    privacy: {
      consentPolicyVersion: "2026.06-th-en-v1",
      contactRevealPolicy: "Contact handles reveal only after explicit mutual consent.",
      deletionRequests: [{ id: "del-1", status: "processing", createdAt: "2026-06-05" }],
    },
    retention: {
      auditLogDays: 730,
      analyticsDays: 365,
      contactRevealDays: 30,
      openQuestions: ["Exact retention for raw moderation snapshots pending legal review."],
    },
    auditLog: [
      {
        id: "aud-1",
        actor: "Mali Manager",
        action: "social_rules_updated",
        entity: "Venue",
        entityId: "venue-smoke",
        createdAt: now,
        metadata: { pii: false },
      },
      {
        id: "aud-2",
        actor: "Aran Owner",
        action: "qr_rotated",
        entity: "Table",
        entityId: "tbl-3",
        createdAt: now,
        metadata: { stalePreviousToken: true },
      },
    ],
  };
}

function cryptoSafe(index: number): string {
  return `${index.toString(36)}_${(index * 7919).toString(36)}_${(index * 15485863).toString(36)}`;
}
